import React, { useState, useEffect } from 'react';
import { GraduationCap, RefreshCw, X, Calendar, CheckCircle } from 'lucide-react';
import { trainingRecordApi } from '../services/api';
import { TrainingRecord } from '../types';
import { useAuth } from '../context/AuthContext';
import { formatCompanyDate } from '../utils/companyDateFormat';

const MyTrainings: React.FC = () => {
  const { employee } = useAuth();
  const [trainings, setTrainings] = useState<TrainingRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedTraining, setSelectedTraining] = useState<TrainingRecord | null>(null);
  const [showDetail, setShowDetail] = useState(false);

  // Training has its own status (backend auto-updates from cycles; Completed is admin-only).
  const getDisplayStatus = (r: TrainingRecord): string => r.status || 'Pending';

  const getCycleStatusBreakdown = (r: TrainingRecord): string => {
    const cycles = r.cycles || [];
    if (cycles.length === 0) return '0 cycles';
    const byStatus: Record<string, number> = {};
    cycles.forEach((c) => {
      const s = (c.status || 'Pending');
      byStatus[s] = (byStatus[s] || 0) + 1;
    });
    return `${cycles.length} cycle${cycles.length !== 1 ? 's' : ''}: ${Object.entries(byStatus).map(([k, v]) => `${v} ${k}`).join(', ')}`;
  };

  const fetchTrainings = async () => {
    if (!employee?.id) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await trainingRecordApi.getByEmployeeId(employee.id);
      setTrainings(Array.isArray(data) ? data : []);
    } catch (error: any) {
      console.error('Error fetching trainings:', error);
      setTrainings([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchTrainings();
  }, [employee?.id]);

  const onRefresh = () => {
    setRefreshing(true);
    fetchTrainings();
  };

  const openDetail = (record: TrainingRecord) => {
    setSelectedTraining(record);
    setShowDetail(true);
  };

  const closeDetail = () => {
    setSelectedTraining(null);
    setShowDetail(false);
  };

  const formatFrequency = (trainingType?: { frequency?: string; recurringValue?: number; recurringUnit?: string }) => {
    if (!trainingType?.frequency) return null;
    if (trainingType.frequency === 'One-Time') return 'One-Time';
    if (trainingType.frequency === 'Recurring' && trainingType.recurringValue && trainingType.recurringUnit) {
      const val = trainingType.recurringValue;
      const unit = trainingType.recurringUnit === 'days' ? (val === 1 ? 'day' : 'days')
        : trainingType.recurringUnit === 'weeks' ? (val === 1 ? 'week' : 'weeks')
        : trainingType.recurringUnit === 'months' ? (val === 1 ? 'month' : 'months')
        : trainingType.recurringUnit;
      return `Every ${val} ${unit}`;
    }
    return trainingType.frequency;
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'inprogress':
      case 'in progress':
        return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'pending':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'overdue':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-neutral-50 text-neutral-700 border-neutral-200';
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-neutral-800">My Trainings</h1>
        <button
          onClick={onRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-neutral-500">Loading trainings...</div>
        </div>
      ) : trainings.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <GraduationCap className="h-16 w-16 text-neutral-300 mx-auto mb-4" />
          <p className="text-neutral-500">No trainings assigned to you.</p>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {trainings.map((record) => (
            <div
              key={record.id}
              onClick={() => openDetail(record)}
              className="bg-white rounded-lg shadow border border-neutral-200 p-4 hover:shadow-md hover:border-blue-200 cursor-pointer transition-all"
            >
              <div className="flex justify-between items-start mb-2">
                <h3 className="font-semibold text-neutral-900">
                  {record.trainingType?.name || `Training #${record.id}`}
                </h3>
                <span
                  className={`px-2 py-1 text-xs font-medium rounded border ${getStatusColor(getDisplayStatus(record))}`}
                >
                  {getDisplayStatus(record)}
                </span>
              </div>
              {record.trainingType?.frequency === 'Recurring' && (
                <p className="text-xs text-neutral-500 mb-2">{getCycleStatusBreakdown(record)}</p>
              )}
              {record.trainingType?.description && (
                <p className="text-sm text-neutral-600 line-clamp-2 mb-3">
                  {record.trainingType.description}
                </p>
              )}
              <div className="flex items-center gap-4 text-xs text-neutral-500">
                {record.dueDate && (
                  <span className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Due: {formatCompanyDate(record.dueDate)}
                  </span>
                )}
                {record.trainingType?.frequency !== 'Recurring' && record.completionDate && (
                  <span className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="h-3.5 w-3.5" />
                    Completed: {formatCompanyDate(record.completionDate)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {showDetail && selectedTraining && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={closeDetail} />
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-neutral-900">
                  {selectedTraining.trainingType?.name || `Training #${selectedTraining.id}`}
                </h2>
                <button
                  onClick={closeDetail}
                  className="p-2 text-neutral-500 hover:text-neutral-700 rounded"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {selectedTraining.trainingType?.description && (
                <p className="text-neutral-600 mb-4">{selectedTraining.trainingType.description}</p>
              )}

              <div className="space-y-2 mb-4">
                <p className="text-sm">
                  <span className="font-medium text-neutral-700">Status:</span>{' '}
                  <span className={`px-2 py-0.5 rounded border text-xs font-medium ${getStatusColor(getDisplayStatus(selectedTraining))}`}>
                    {getDisplayStatus(selectedTraining)}
                  </span>
                </p>
                {selectedTraining.trainingType?.frequency === 'Recurring' && (
                  <p className="text-sm text-neutral-600">{getCycleStatusBreakdown(selectedTraining)}</p>
                )}
                {selectedTraining.dueDate && (
                  <p className="text-sm text-neutral-600">
                    <span className="font-medium">Due:</span>{' '}
                    {formatCompanyDate(selectedTraining.dueDate)}
                  </p>
                )}
                {selectedTraining.trainingType?.frequency !== 'Recurring' && selectedTraining.completionDate && (
                  <p className="text-sm text-green-600">
                    <span className="font-medium">Completed:</span>{' '}
                    {formatCompanyDate(selectedTraining.completionDate)}
                  </p>
                )}
                {formatFrequency(selectedTraining.trainingType) && (
                  <p className="text-sm text-neutral-600">
                    <span className="font-medium">Frequency:</span> {formatFrequency(selectedTraining.trainingType)}
                  </p>
                )}
              </div>

              {selectedTraining.trainingType?.frequency === 'Recurring' && selectedTraining.cycles && selectedTraining.cycles.length > 0 && (
                <div className="border-t border-neutral-200 pt-4 mt-4">
                  <h3 className="font-medium text-neutral-800 mb-2">Cycles</h3>
                  <ul className="space-y-2 text-sm text-neutral-600">
                    {selectedTraining.cycles.map((c) => (
                      <li key={c.id} className="flex justify-between items-start">
                        <span>
                          Due: {c.dueDate ? formatCompanyDate(c.dueDate) : 'N/A'} — {c.status}
                          {c.completionDate && ` (Completed ${formatCompanyDate(c.completionDate)})`}
                        </span>
                        {c.comment && <span className="text-neutral-500 truncate max-w-[150px]" title={c.comment}>{c.comment}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedTraining.trainingType?.frequency !== 'Recurring' && selectedTraining.comment && (
                <div className="border-t border-neutral-200 pt-4">
                  <h3 className="font-medium text-neutral-800 mb-2">Comments</h3>
                  <p className="text-sm text-neutral-600">{selectedTraining.comment}</p>
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  onClick={closeDetail}
                  className="px-4 py-2 bg-neutral-100 text-neutral-700 rounded-md hover:bg-neutral-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTrainings;
